<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-sm-6">
                <div class="search-box mr-2 mb-2 d-inline-block">
                  <div class="position-relative">
                    <input
                      type="text"
                      class="form-control"
                      v-model="search"
                      placeholder="Buscar formulários..."
                      @keyup.enter="doSearch"
                    />
                    <i class="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="float-sm-end" v-if="$can('formbuilder.add')">
                  <button
                    type="button"
                    class="btn btn-success btn-rounded mb-2"
                    @click="newForm"
                  >
                    <i class="mdi mdi-plus mr-1"></i> Novo Formulário
                  </button>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-centered table-nowrap table-hover">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Slug</th>
                    <th>Campos</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="form in forms" :key="form.id">
                    <td>
                      <strong>{{ form.name }}</strong>
                      <br />
                      <small class="text-muted">{{ form.description }}</small>
                    </td>
                    <td>
                      <code>{{ form.slug }}</code>
                    </td>
                    <td>
                      <span class="badge badge-soft-primary">
                        {{ form.fields_count || 0 }} campos
                      </span>
                    </td>
                    <td>
                      <span
                        class="badge badge-pill font-size-12"
                        :class="{
                          'badge-soft-success': form.status === 1,
                          'badge-soft-danger': form.status === 0
                        }"
                      >
                        {{ form.status === 1 ? 'Ativo' : 'Inativo' }}
                      </span>
                    </td>
                    <td>
                      {{ formatDate(form.created_at) }}
                    </td>
                    <td>
                      <b-dropdown
                        class="card-drop"
                        variant="white"
                        right
                        toggle-class="p-0"
                        v-if="$canAny(['formbuilder.edit', 'formbuilder.delete'])"
                      >
                        <template v-slot:button-content>
                          <i class="mdi mdi-dots-horizontal font-size-18"></i>
                        </template>
                        <b-dropdown-item @click="editForm(form.id)" v-if="$can('formbuilder.edit')">
                          <i class="fas fa-pencil-alt text-success mr-1"></i>
                          Editar
                        </b-dropdown-item>
                        <b-dropdown-item @click="duplicateForm(form.id)" v-if="$can('formbuilder.add')">
                          <i class="fas fa-copy text-primary mr-1"></i>
                          Duplicar
                        </b-dropdown-item>
                        <b-dropdown-item @click="toggleStatus(form.id)" v-if="$can('formbuilder.edit')">
                          <i
                            class="fas fa-circle mr-1"
                            :class="{
                              'text-danger': form.status === 1,
                              'text-success': form.status === 0
                            }"
                          ></i>
                          {{ form.status === 1 ? 'Inativar' : 'Ativar' }}
                        </b-dropdown-item>
                        <b-dropdown-divider v-if="$can('formbuilder.delete')"></b-dropdown-divider>
                        <b-dropdown-item @click="deleteFormConfirm(form.id)" v-if="$can('formbuilder.delete')">
                          <i class="fas fa-trash-alt text-danger mr-1"></i>
                          Excluir
                        </b-dropdown-item>
                      </b-dropdown>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="forms.length === 0 && !loading" class="text-center py-5">
                <i class="bx bx-file font-size-48 text-muted"></i>
                <p class="text-muted mt-2">Nenhum formulário encontrado</p>
                <button class="btn btn-primary" @click="newForm" v-if="$can('formbuilder.add')">
                  <i class="mdi mdi-plus mr-1"></i> Criar primeiro formulário
                </button>
              </div>
            </div>

            <div class="row" v-if="pagination.total > 0">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="pagination.total"
                      :per-page="perPage"
                      @input="updateList"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Swal from 'sweetalert2'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapGetters } from 'vuex'
import permissionMixin from '@/mixins/permissionMixin'

export default {
  name: 'FormBuilderIndex',
  mixins: [permissionMixin],
  components: {
    Layout,
    PageHeader
  },
  page: {
    title: 'Form Builder',
    meta: [
      {
        name: 'description',
        content: appConfig.description
      }
    ]
  },
  data () {
    return {
      loading: false,
      search: '',
      currentPage: 1,
      perPage: 20,
      title: 'Form Builder',
      items: [
        { text: 'Form Builder' },
        { text: 'Listagem', active: true }
      ]
    }
  },
  computed: {
    ...mapGetters('formBuilder', ['FORMS', 'PAGINATION']),
    forms () {
      return this.FORMS || []
    },
    pagination () {
      return this.PAGINATION || { total: 0 }
    }
  },
  mounted () {
    this.updateList()
  },
  methods: {
    ...mapActions('formBuilder', [
      'GetForms',
      'ChangeFormStatus',
      'DuplicateForm',
      'DeleteForm'
    ]),

    formatDate (date) {
      if (!date) return '-'
      return this.$moment(date).format('DD/MM/YYYY HH:mm')
    },

    async updateList () {
      this.loading = true
      try {
        await this.GetForms({
          search: this.search || null,
          limit: this.perPage,
          page: this.currentPage
        })
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao carregar formulários')
      } finally {
        this.loading = false
      }
    },

    doSearch () {
      this.currentPage = 1
      this.updateList()
    },

    newForm () {
      this.$router.push({ name: 'form-builder-new' })
    },

    editForm (id) {
      this.$router.push({ name: 'form-builder-edit', params: { id } })
    },

    async toggleStatus (id) {
      try {
        await this.ChangeFormStatus(id)
        successMessage('Status atualizado com sucesso!', 2500)
        this.updateList()
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao atualizar status')
      }
    },

    async duplicateForm (id) {
      try {
        const loader = this.$loading.show()
        await this.DuplicateForm(id)
        loader.hide()
        successMessage('Formulário duplicado com sucesso!', 2500)
        this.updateList()
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao duplicar formulário')
      }
    },

    deleteFormConfirm (id) {
      Swal.fire({
        title: 'Você tem certeza?',
        text: 'Esta ação não poderá ser revertida',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'CANCELAR',
        confirmButtonText: 'SIM, EXCLUIR!'
      }).then(async (result) => {
        if (result.value) {
          try {
            await this.DeleteForm(id)
            successMessage('Formulário excluído com sucesso!', 2500)
            this.updateList()
          } catch (err) {
            console.error(err)
            errorMessage('Erro ao excluir formulário')
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.table-responsive {
  min-height: 300px;
}
.float-sm-end {
  float: right;
}
.search-icon {
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}
</style>
